import React, { useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { Form, Formik, FormikHelpers } from 'formik';

import { Reload, ShipToHome } from 'components/custom-icons';

import { ProductAmountInput, ProductAmountInputFormData } from './ProductAmountInput';
import { CartProductTabPanelProps } from './types';
import { sortOptionValuesAscending } from './utils';

import { createStyle, createStyles } from 'utils/createStyle';
import useUnavailableProductVariants from './utils/useUnavailableProductVariants';
import { waitPromise } from 'utils/promiseHelpers';
import { FormBtn } from 'components/form-btn';
import { useRootSelector } from 'common/features/featuresReducer';
import { selectOrganizationOptionsFinishedLoading } from 'common/features/store/duck/organization/duck';
import { FadeIn } from 'components/loading-screen/FadeIn';
import { Render } from 'components/loading-screen/Render';
import { SkeletonProps } from 'react-loading-skeleton';
import { Product, ProductVariant } from 'common/api/e-comm/models/Product';
import { Alert, AlertType } from 'components/alerts/sticky/Alert';

export interface ProductDetailsPanelProps extends CartProductTabPanelProps {
    value: string;
    isShipToCoordinatorEnabled?: boolean;
    brandName: string;
}

const styles = createStyles({
    ctrlIncrement: createStyle('inline-block', {
        maxWidth: '140px',
    }),
    incrementAndTotal: createStyle('grid mt-4 lg:mt-0', {
        gridTemplateColumns: 'auto auto',
    }),
    listShipToHome: createStyle('mt-2 text-xs text-grey-darkest', {
        listStyleType: 'disc',
        paddingLeft: '20px',
    }),
    listComboContainer: createStyle('w-full', { maxWidth: '220px' }),
});

export const PhysicalTabPanel = ({
    value,
    product,
    addToCart,
    canCheckout,
    isShipToCoordinatorEnabled,
    brandName,
}: ProductDetailsPanelProps) => {
    const { text, variants: unavailableVariants } = useUnavailableProductVariants(product);
    const orgOptionsHaveLoaded = useRootSelector(selectOrganizationOptionsFinishedLoading);
    const [selectedVariant, setSelectedVariant] = useState<ProductVariant | undefined>(undefined);

    const initialValues: ProductAmountInputFormData = {
        selectedOptions: Object.fromEntries(
            product.options.map((opt) => [opt.id, sortOptionValuesAscending(opt.values)[0].value])
        ),
        priceOverride: 0,
        quantity: 1,
    };

    const [addingToCart, setAddingToCart] = useState(false);

    const allVariantsUnavailable = unavailableVariants.length === product.variants.length;

    const onSubmit = async (
        data: ProductAmountInputFormData,
        formikHelpers: FormikHelpers<ProductAmountInputFormData>
    ) => {
        // CASE: if you're not part of the organization anymore -- you shouldn't
        // be able to add items to cart
        if (!canCheckout) {
            return;
        }

        const apiResponse = await waitPromise(addToCart(product, data), setAddingToCart);

        // reset the form to the initial state
        if (apiResponse?.response?.ok) {
            formikHelpers.resetForm();
        }
    };
    const productDisabled =
        !product.isShipToHomeEligible && !isShipToCoordinatorEnabled && orgOptionsHaveLoaded;
    const productEnabled =
        (isShipToCoordinatorEnabled && orgOptionsHaveLoaded) || product.isShipToHomeEligible;

    return (
        <Tabs.Panel value={value}>
            <div
                className="mb-24"
                data-cnstrc-product-detail
                data-cnstrc-item-id={product.brandId}
                data-cnstrc-item-variation-id={selectedVariant?.id}
                data-cnstrc-item-name={brandName}
                data-cnstrc-item-price={selectedVariant?.price}
            >
                {text ? (
                    <Alert
                        type={AlertType.INFO}
                        className="my-5 sm:mt-0"
                        title="Temporarily Unavailable"
                    >
                        <p>{text}</p>
                    </Alert>
                ) : null}
                <Render<SkeletonProps>
                    when={orgOptionsHaveLoaded}
                    loadingComponentProps={{ className: 'h-36 sm:h-56' }}
                >
                    <FadeIn when={productDisabled}>
                        <div className="flex flex-col text-grey-darkest shadow-lg border-solid border rounded-lg border-brand px-5 py-4 mt-5 mb-4">
                            <p className="text-md">
                                This card is not available because your organization only allows
                                Ship to Home eligible physical gift cards. If you would like to
                                place an order for this card, please contact your coordinator.
                            </p>
                        </div>
                    </FadeIn>
                    <FadeIn when={productEnabled}>
                        <Formik initialValues={initialValues} onSubmit={onSubmit} key={product.id}>
                            <Form>
                                <div className="text-grey-darkest">
                                    Gift cards that are shipped to your organization, or mailed
                                    directly to you if ship-to-home eligible.
                                </div>
                                <ProductInfoList product={product} />
                                <div className="mt-8">
                                    {!allVariantsUnavailable && (
                                        <ProductAmountInput
                                            product={product}
                                            customLabel={(label) => (
                                                <span className="font-bold">{label}</span>
                                            )}
                                            hideQuantityField={false}
                                            uiSettings={{
                                                totalMargin: 'mt-auto sm:mt-0 sm:mb-auto',
                                            }}
                                            setVariant={setSelectedVariant}
                                        />
                                    )}
                                </div>
                                <div className="block text-center sm:text-right mt-14 mb-8">
                                    {!allVariantsUnavailable && (
                                        <FormBtn
                                            disabled={!canCheckout}
                                            className="pdp_add_to_cart_button w-80 md:w-48 sm:ml-4"
                                            isSubmitting={addingToCart}
                                        >
                                            Add to Cart
                                        </FormBtn>
                                    )}
                                </div>
                            </Form>
                        </Formik>
                    </FadeIn>
                </Render>
            </div>
        </Tabs.Panel>
    );
};

const ProductInfoList = ({ product }: { product: Product }) => {
    if (!(product.isShipToHomeEligible || product.isReloadable)) {
        // NOTE: Don't render the list if none of the items can be renderable
        return null;
    }
    return (
        <ul className={styles.listShipToHome}>
            {product.isShipToHomeEligible && (
                <li className="pdp_ship_to_home_tag">
                    <ShipToHome height="0.6rem" /> Ship to home eligible
                </li>
            )}
            {product.isReloadable && (
                <li className="mt-1">
                    <Reload width="1.3rem" height="0.8rem" /> Reloadable card
                </li>
            )}
        </ul>
    );
};
